import { addDays, epochDayOf, parseDate, weekStartOf } from './date';

/** Calendar-month arithmetic on `YYYY-MM` strings (the month view's route parameter). */

const MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;

export function isValidMonth(value: string): boolean {
  return MONTH_PATTERN.test(value);
}

/** The month (`YYYY-MM`) a calendar date falls in. */
export function monthOf(date: string): string {
  parseDate(date);
  return date.slice(0, 7);
}

/** First day of the month, `YYYY-MM-01`. */
export function monthStart(month: string): string {
  if (!isValidMonth(month)) throw new RangeError(`Invalid month: ${month}`);
  return `${month}-01`;
}

/** Last day of the month. */
export function monthEnd(month: string): string {
  return addDays(monthStart(addMonths(month, 1)), -1);
}

/** The month `count` months after `month` (negative for earlier months). */
export function addMonths(month: string, count: number): string {
  const { year, month: m } = parseDate(monthStart(month));
  const index = year * 12 + (m - 1) + count;
  const nextYear = Math.floor(index / 12);
  return `${String(nextYear).padStart(4, '0')}-${String((index % 12) + 1).padStart(2, '0')}`;
}

export function daysInMonth(month: string): number {
  return epochDayOf(monthStart(addMonths(month, 1))) - epochDayOf(monthStart(month));
}

/** The date with the same day number in `month`, or the month's last day when it has fewer days. */
export function sameDayInMonth(date: string, month: string): string {
  const { day } = parseDate(date);
  const candidate = Math.min(day, daysInMonth(month));
  return `${month}-${String(candidate).padStart(2, '0')}`;
}

/**
 * The weeks (Monday first) a month grid shows: each row has 7 entries, a date of `month` or
 * null for the placeholder cells before the 1st and after the last day.
 */
export function monthWeeks(month: string): (string | null)[][] {
  const first = monthStart(month);
  const last = monthEnd(month);
  const weeks: (string | null)[][] = [];
  for (let start = weekStartOf(first); start <= last; start = addDays(start, 7)) {
    const row: (string | null)[] = [];
    for (let i = 0; i < 7; i += 1) {
      const date = addDays(start, i);
      row.push(date >= first && date <= last ? date : null);
    }
    weeks.push(row);
  }
  return weeks;
}
